import { useCallback, useState } from 'react';

/**
 * The user's opt-in to the FDA Submission View (Beta) — the linked
 * Label ⇄ HA-Document split-dropdowns on the drug page.
 *
 * Replaces the removed build-time flag: the view is
 * a per-browser user choice, defaulting **ON** as of 2026-08-17-b — the soft
 * launch is over and submission view is the intended default experience.
 *
 * Storage is best-effort. Safari private mode and disabled-storage profiles
 * throw on both read and write. The lazy initializer defaults OFF if reading
 * fails. The setter updates React state first, so the user's choice survives
 * route changes in this mounted SPA session even if persistence fails.
 *
 * This is desired request state only. Active UI mode still comes solely from
 * the last committed response (`sbaCycleInfo != null`).
 */
// v2 (2026-08-17-c). The key is BUMPED rather than the stored value ignored.
//
// The v1 key holds opt-outs made while submission view was an admin-only beta
// behind a "Switch to FDA Submission View (BETA)" button. Those 'false' values
// were a judgement about a beta, not about the GA default, and honouring them
// pinned exactly the people who tested the beta into classic view forever.
//
// Bumping orphans them, so every browser starts fresh at the new default (ON)
// while the read/write logic stays intact — an opt-out made from here on is
// still remembered, and classic view remains a reachable, testable state.
// Reading the value unconditionally instead would have made classic
// unreachable, which is a different (and worse) change than it looks.
export const SUBMISSION_VIEW_PREFERENCE_KEY = 'labelogy-fda-submission-view-v2';

function readInitialPreference(): boolean {
  try {
    // `!== 'false'` not `=== 'true'`: the default is ON, so an absent key
    // (never chose, incl. every browser after the v2 key bump) opts IN, while
    // an explicit 'false' written from here on is still honoured.
    return localStorage.getItem(SUBMISSION_VIEW_PREFERENCE_KEY) !== 'false';
  } catch {
    // Storage unreadable (Safari private mode) must land on the DEFAULT.
    // Returning false would strand exactly those profiles on classic.
    return true;
  }
}

export function useSubmissionViewPreference(): readonly [boolean, (value: boolean) => void] {
  const [enabled, setEnabled] = useState(readInitialPreference);

  const setPreference = useCallback((value: boolean) => {
    setEnabled(value);
    try {
      localStorage.setItem(SUBMISSION_VIEW_PREFERENCE_KEY, String(value));
    } catch {
      // State remains updated for this mounted SPA session.
    }
  }, []);

  return [enabled, setPreference] as const;
}
